import * as Base64 from "../bindings/Base64.bs";
import { allPatches, statsSet } from "common/src/Config";
import {
  items,
  augments,
  itemsArr,
  augmentsArr,
} from "common/src/data/Data14.bs";

import {
  items as items2,
  itemsArr as itemsArr2,
} from "common/src/data/Data14.bs";
import * as Sd from "common/src/tft-static/Sd";
import * as T from "common/src/bindings/Translation2";

export type GameMode = "ranked" | "duo" | "hr" | "temp" | "pbe";

const assignLabel = (set: number, filter: Filter) => {
  if (filter.typ === "u") {
    filter.label = T.units(filter.id);
  } else if (filter.typ === "t") {
    const count = Sd.traitRankUnitCount(
      filter.id,
      Number(filter.rank.replace(/\+-/, "")),
      set,
    );
    if (filter.rank != "0") {
      filter.label = count + " " + T.traits(filter.id);
    } else {
      filter.label = T.traits(filter.id);
    }
  } else if (filter.typ === "i") {
    filter.label = T.items(filter.id);
  } else if (filter.typ === "a") {
    filter.label = T.augments(filter.id);
  }
};

export const allRegions: Array<[string, string]> = [
  ["euw1", "EUW"],
  ["eun1", "EUNE"],
  ["na1", "NA"],
  ["la1", "LAN"],
  ["la2", "LAS"],
  ["me1", "ME"],
  ["oc1", "OCE"],
  ["tr1", "TR"],
  ["ru", "RU"],
  ["kr", "KR"],
  ["jp1", "JP"],
  ["br1", "BR"],
  ["ph2", "PH"],
  ["sg2", "SEA"],
  ["th2", "TH"],
  ["tw2", "TW"],
  ["vn2", "VN"],
];

export type FilterUnit = {
  typ: "u";
  id: string;
  sid: string;
  starLevel: number;
  label: string;
  itemCount: undefined | string;
};
export type FilterTrait = {
  typ: "t";
  id: string;
  sid: string;
  rank: string;
  label: string;
};
export type FilterItem = {
  typ: "i";
  id: string;
  sid: string;
  unitId: undefined | [string, number];
  label: string;
};
export type FilterAugment = {
  typ: "a";
  id: string;
  sid: string;
  pick: number;
  label: string;
};
export type Filter = FilterUnit | FilterTrait | FilterItem | FilterAugment;

const itemCountOptions = ["_", "0", "1", "+1", "-1", "2", "+2", "-2", "3"];

function encodeFilter(set: number, filter: Filter): string {
  if (filter.typ === "u") {
    const allUnits = Sd.allUnits3(set);
    const unit_idx = allUnits.indexOf(filter.id);
    // id: 7b, sl: 3, ic: 4
    const ftyp =
      (0 << 15) |
      (unit_idx << 7) |
      (filter.starLevel << 4) |
      itemCountOptions.indexOf(filter.itemCount || "_");

    return Base64.encode(ftyp, 3);
  } else if (filter.typ === "t") {
    const allTraits = Sd.allTraits(set);
    let rank;
    let plusMinus;
    if (filter.rank[0] === "+") {
      plusMinus = 2;
      rank = Number(filter.rank.slice(1));
    } else if (filter.rank[0] === "-") {
      plusMinus = 1;
      rank = Number(filter.rank.slice(1));
    } else {
      plusMinus = 0;
      rank = Number(filter.rank);
    }
    // For backwards compat
    const adjRank = rank == 0 ? 7 : rank - 1;
    const ftyp =
      (1 << 15) |
      (allTraits.indexOf(filter.id) << 6) |
      (adjRank << 2) |
      plusMinus;
    return Base64.encode(ftyp, 3);
  } else if (filter.typ === "i") {
    const itemInfo = items[filter.id] || items2[filter.id];
    let ftyp = (2 << 21) | (itemInfo.id << 10);
    if (filter.unitId) {
      const allUnits = Sd.allUnits3(set);
      const unitIdx = allUnits.indexOf(filter.unitId[0]);
      ftyp = ftyp | ((unitIdx + 1) << 3 || filter.unitId[1]);
    }
    return Base64.encode(ftyp, 4);
  } else if (filter.typ === "a") {
    const augInfo = augments[filter.id];
    const ftyp = (3 << 15) | (augInfo.id << 3) | filter.pick;
    return Base64.encode(ftyp, 3);
  }

  // Never happens but TS complains otherwise
  return "";
}

function decodeFilter(set: number, str: string, idx: number): [Filter, number] {
  const b1 = Base64.decode(str[idx]);
  const b2 = Base64.decode(str[idx + 1]);
  const b3 = Base64.decode(str[idx + 2]);

  const typ = b1 >> 3;
  if (typ === 0) {
    const allUnits = Sd.allUnits3(set);
    const code = (b1 << 12) | (b2 << 6) | b3;
    const icIdx = code & 0xf;
    const slIdx = (code >> 4) & 0x7;
    const unitIdx = (code >> 7) & 0x7f;
    const unitId = allUnits[unitIdx];
    const itemCount = itemCountOptions[icIdx];

    return [
      {
        typ: "u",
        id: unitId,
        sid: unitId,
        starLevel: slIdx,
        label: T.units(unitId),
        itemCount: itemCount === "_" ? undefined : itemCount,
      },
      3,
    ];
  } else if (typ === 1) {
    const allTraits = Sd.allTraits(set);
    const code = (b1 << 12) | (b2 << 6) | b3;
    let pmOut = "";
    let pm = code & 0x3;
    if (pm === 1) {
      pmOut = "-";
    } else if (pm === 2) {
      pmOut = "+";
    }
    const adjRankPart = (code >> 2) & 0x7;
    // Backwards compat
    const rankPart = adjRankPart === 7 ? 0 : adjRankPart + 1;
    const rank = pmOut + rankPart;
    const traitIdx = (code >> 6) & 0x1f;
    const traitId = allTraits[traitIdx];

    return [
      {
        typ: "t",
        id: traitId,
        sid: traitId,
        rank: rank,
        label: "",
      },
      3,
    ];
  } else if (typ === 2) {
    const allUnits = Sd.allUnits3(set);
    const b4 = Base64.decode(str[idx + 3]);
    const code = (b1 << 18) | (b2 << 12) | (b3 << 6) | b4;
    const itemId = (code >> 10) & 0xff;
    const [itemSid, _itemInfo] = itemsArr()[itemId] || itemsArr2()[itemId];
    const starLevel = code & 0x7;
    const unitIdx = (code >> 3) & 0x7f;

    return [
      {
        typ: "i",
        id: itemSid,
        sid: itemSid,
        label: "",
        unitId: unitIdx > 0 ? [allUnits[unitIdx - 1], starLevel] : undefined,
      },
      4,
    ];
  } else if (typ === 3) {
    const code = (b1 << 12) | (b2 << 6) | b3;
    const pick = code & 0x7;
    const augId = (code >> 3) & 0xfff;
    const [augSid, _augInfo] = augmentsArr()[augId];

    return [
      {
        typ: "a",
        id: augSid,
        sid: augSid,
        pick: pick,
        label: "",
      },
      3,
    ];
  } else {
    throw new Error("Unknown combos decode filter type " + typ);
  }
}

type Config = {
  // patch = number from latest (can omit if latest), pack into same byte
  patch: number;
  // rank_group = 6 options, 3 bytes, pack same as option_id
  rankGroup: number;
  // levels = byte_array, 1 byte
  levels: Array<number>;
  // will need to actually be a query param
  lastRound: string | null;
  // will need to actually be a query param
  itemCount: string | null;
  // typ + 3 bitsfor amount of portals then 1 bit per portal
  portals: Array<number>;
  // typ + 3 bits for amount of regions then 1 bit per region
  regions: Array<string>;
  // 3 possible values, pack into the same byte
  gameMode: GameMode;

  gameType: number;

  date: number;
};

function encodeConfig(config: Config): string {
  let encodedCount = 0;
  let encoded = "";

  const patchIdx = allPatches.indexOf(config.patch);
  if (patchIdx > 0) {
    const d = (0 << 3) | (patchIdx - 1);
    encoded += Base64.encode(d, 1);
    encodedCount += 1;
  }

  if (config.rankGroup !== 1) {
    const d = (1 << 3) | config.rankGroup;
    encoded += Base64.encode(d, 1);
    encodedCount += 1;
  }

  if (config.levels.length > 0) {
    let levels = 0;
    for (let level of config.levels) {
      levels = levels | (1 << (level - 5));
    }
    encoded += Base64.encode(2 << 3, 1);
    encoded += Base64.encode(levels, 1);
    encodedCount += 1;
  }

  if (config.portals.length > 0) {
    let numChunks = Math.floor((config.portals.length - 1) / 8) + 1;
    let cfg = "";
    for (let chunk = 0; chunk < numChunks; chunk++) {
      const portals = config.portals.slice(chunk * 8, (chunk + 1) * 8);
      cfg += Base64.encode((3 << 3) | (portals.length - 1));
      for (let portal of portals) {
        cfg += Base64.encode(portal, 1);
      }
      encodedCount += 1;
    }
    encoded += cfg;
  }

  if (config.regions.length > 0) {
    let numChunks = Math.floor((config.regions.length - 1) / 8) + 1;
    let cfg = "";
    for (let chunk = 0; chunk < numChunks; chunk++) {
      const regions = config.regions.slice(chunk * 8, (chunk + 1) * 8);
      cfg += Base64.encode((4 << 3) | (regions.length - 1));
      for (let region of regions) {
        const regionId = allRegions.findIndex(([id, _], idx) => id == region);
        cfg += Base64.encode(regionId, 1);
      }
      encodedCount += 1;
    }
    encoded += cfg;
  }

  if (config.gameMode !== "ranked") {
    const id = ["duo", "hr", "temp", "pbe"].indexOf(config.gameMode);
    encoded += Base64.encode((5 << 3) | id);
    encodedCount += 1;
  }

  if (config.gameType !== 0) {
    encoded += Base64.encode(6 << 3);
    encoded += Base64.encode(config.gameType);
    encodedCount += 1;
  }

  if (config.date !== -1) {
    encoded += Base64.encode((7 << 3) | config.date);
    encodedCount += 1;
  }

  if (encodedCount > 0) {
    return Base64.encode(0 << 4 || encodedCount - 1, 1) + encoded;
  } else {
    return "";
  }
}

const defaultConfig: Config = {
  patch: allPatches[0],
  rankGroup: 1,
  levels: [],
  // Add as query params
  lastRound: null,
  // Add as query params
  itemCount: null,
  portals: [],
  regions: [],
  gameMode: "ranked",
  gameType: 0,
  date: -1,
};

function decodeConfig(path: string, length: number): [Config, number] {
  const config = defaultConfig;

  if (typeof window !== "undefined") {
    const search = window.location.search;
    for (let param of search.slice(1).split("&")) {
      const [k, v] = param.split("=");
      if (k === "lr") {
        config.lastRound = v;
      } else if (k === "ic") {
        config.itemCount = v;
      }
    }
  }

  // Idx tracks chars decoded
  let idx = 0;
  // configIdx tracks config items decoded
  let configIdx = 0;
  while (configIdx < length) {
    const v0 = Base64.decode(path[idx]);
    const typ = v0 >> 3;
    switch (typ) {
      case 0: {
        const patchIdx = (v0 & 0x7) + 1;
        config.patch = allPatches[patchIdx];
        idx += 1;
        configIdx += 1;
        break;
      }
      case 1: {
        const rankGroup = v0 & 0x7;
        config.rankGroup = rankGroup;
        idx += 1;
        configIdx += 1;
        break;
      }
      case 2: {
        let levelsInput = Base64.decode(path[idx + 1]);
        const levels = [];
        for (let i = 0; i < 6; i++) {
          if ((levelsInput & 0b1) == 1) {
            levels.push(i + 5);
          }
          levelsInput = levelsInput >> 1;
        }
        config.levels = levels;
        idx += 2;
        configIdx += 1;
        break;
      }
      case 3: {
        const len = (v0 & 0x7) + 1;
        const ids = [];
        for (let i = 1; i <= len; i++) {
          ids.push(Base64.decode(path[idx + i]));
        }
        config.portals = ids;
        idx += 1 + len;
        configIdx += 1;
        break;
      }
      case 4: {
        const len = (v0 & 0x7) + 1;
        const ids = [];
        for (let i = 1; i <= len; i++) {
          ids.push(Base64.decode(path[idx + i]));
        }
        config.regions = ids.map((idx) => {
          return allRegions[idx][0];
        });
        idx += 1 + len;
        configIdx += 1;
        break;
      }
      case 5: {
        const id = v0 & 0x7;
        config.gameMode = ["duo", "hr", "temp"][id] as GameMode;
        idx += 1;
        configIdx += 1;
        break;
      }
      case 6: {
        const v1 = Base64.decode(path[idx + 1]);
        config.gameType = v1;
        idx += 2;
        configIdx += 1;
        break;
      }
      case 7: {
        config.date = v0 & 0x7;
        idx += 1;
        configIdx += 1;
        break;
      }
      default: {
        throw new Error(`Unknown idx ${typ}`);
      }
    }
  }

  return [config, idx];
}

const version = "A";
export function encodeQuery(
  set: number,
  config: Config,
  includeFilters: Array<Filter>,
  excludeFilters: Array<Filter>,
) {
  let includes = "";
  if (includeFilters.length > 0) {
    let filters = includeFilters.slice(0);
    while (filters.length > 0) {
      includes += Base64.encode((1 << 4) | Math.min(filters.length - 1, 15));
      for (let filter of filters.slice(0, 16)) {
        includes += encodeFilter(set, filter);
      }

      filters = filters.slice(16);
    }
  }

  let excludes = "";
  if (excludeFilters.length > 0) {
    let filters = excludeFilters.slice(0);
    while (filters.length > 0) {
      includes += Base64.encode((2 << 4) | Math.min(filters.length - 1, 15));
      for (let filter of filters.slice(0, 16)) {
        includes += encodeFilter(set, filter);
      }

      filters = filters.slice(16);
    }
  }
  // A char signifying amount of include filters
  // If exclude filters present then a char signifying amount of exclude filters, empty = 0

  const queryArr = [];
  if (config.lastRound) {
    queryArr.push(["lr", config.lastRound]);
  }
  if (config.itemCount) {
    queryArr.push(["ic", config.itemCount]);
  }
  let queryStr = "";
  if (queryArr.length > 0) {
    let idx = 0;
    for (let [key, val] of queryArr) {
      queryStr += `${idx === 0 ? "?" : "&"}${key}=${val}`;
      idx += 1;
    }
  }

  const out = version + encodeConfig(config) + includes + excludes + queryStr;
  if (out === version) {
    return "";
  } else {
    return version + encodeConfig(config) + includes + excludes + queryStr;
  }
}

export function decodeQuery(
  initStr: string,
): [Config, Array<Filter>, Array<Filter>] {
  if (initStr[0] !== version) {
    return [defaultConfig, [], []];
  }
  const str = initStr.split("?")[0];
  let currIdx = 1;
  let header;
  let typ;
  let count;

  let config = defaultConfig;
  let includeFilters: Array<Filter> = [];
  let excludeFilters: Array<Filter> = [];

  if (currIdx < str.length) {
    header = Base64.decode(str[currIdx]);
    typ = header >> 4;
    count = (header & 0xf) + 1;
    if (typ === 0) {
      // slice 2 to skip version and config header
      let [cfg, cfgStrCount] = decodeConfig(str.slice(2), count);
      config = cfg;
      currIdx += 1 + cfgStrCount;
    }
  }
  const set = config.gameMode === "temp" ? 46 : statsSet;

  while (true) {
    if (currIdx >= str.length) {
      break;
    }
    header = Base64.decode(str[currIdx]);
    typ = header >> 4;
    count = (header & 0xf) + 1;

    if (typ !== 1) {
      break;
    }
    currIdx += 1;
    for (let idx = 0; idx < count; idx++) {
      const [filter, fc] = decodeFilter(set, str, currIdx);
      assignLabel(set, filter);
      includeFilters.push(filter);
      currIdx += fc;
    }
  }

  while (true) {
    if (currIdx >= str.length) {
      break;
    }
    header = Base64.decode(str[currIdx]);
    typ = header >> 4;
    count = (header & 0xf) + 1;

    if (typ !== 2) {
      break;
    }
    currIdx += 1;
    for (let idx = 0; idx < count; idx++) {
      const [filter, fc] = decodeFilter(set, str, currIdx);
      assignLabel(set, filter);
      excludeFilters.push(filter);
      currIdx += fc;
    }
  }

  return [config, includeFilters, excludeFilters];
}
